/** biome-ignore-all lint/nursery/useUniqueElementIds: <explanation> */
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { tv, type VariantProps } from "tailwind-variants";
import type { InputProps, NumberInputProps } from "../../../core/inputs/Input";
import type {
  InputRow,
  QuestionBaseProps,
} from "../../../special/survey/survey.config";
import { AddButton } from "../components/AddRowButton";
import EditableContent from "../components/EditableContent";
import type { Page, PageType } from "../survey.config";
import { maxItems } from "../utils";

const SelectStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
  },
});

type TextProps<T extends PageType = "textResponse" | "numberInput"> = Page<T> &
  VariantProps<typeof SelectStyles> &
  Omit<QuestionBaseProps<T>, "onChange"> & {
    onChange: ({
      title,
      placeholder,
    }: {
      title?: string;
      placeholder?: string;
    }) => void; // different from other components
    rows?: number;
    maxRows?: number;
    textRequired?: boolean;
    placeholder?: string;
    className?: string;
  };

const TextRow = <T extends "textResponse" | "numberInput">({
  row,
  mode,
  onChange,
  ...props
}: {
  row: InputRow<T>;
} & TextProps) => {
  const [translation] = useGlobalState().translationsState;
  const [error, setError] = useState<string | false>(false);
  const [touched, setTouched] = useState(false);
  const { t } = useTranslation();
  const isNullish = (value: string | number | undefined | null) =>
    value === null || value === undefined || value === "";
  const checkError = (value?: string | number) => {
    const nullish = isNullish(value);
    const val = props.type === "numberInput" && value != null ? +value : value;
    if (props.type === "numberInput" && typeof val === "number") {
      const [min, max] = [
        props.settings?.limit?.min || 0,
        props.settings?.limit?.max || Number.POSITIVE_INFINITY,
      ];
      if (props.settings?.required && nullish) {
        setError(t("survey:disabledMessages.numberInput"));
      } else if (!nullish && min != null && val < min) {
        setError(t("survey:disabledMessages.numberInput.min", { min }));
      } else if (!nullish && max != null && val > max) {
        setError(t("survey:disabledMessages.numberInput.max", { max }));
      } else if (
        !nullish &&
        min != null &&
        max != null &&
        (val < min || val > max)
      ) {
        setError(t("survey:disabledMessages.numberInput.minMax", { min, max }));
      } else {
        setError(false);
      }
    } else if (props.type === "textResponse" && typeof val === "string") {
      if (props.settings?.required && nullish) {
        setError(t("survey:disabledMessages.textResponse"));
      } else if (
        (val?.length || 0) >
        (props.settings?.limitCharacters?.maxChars || Number.POSITIVE_INFINITY)
      ) {
        setError(
          t("survey:disabledMessages.textResponse.maxChar", {
            maxChars: props.settings?.limitCharacters?.maxChars,
          }),
        );
      } else {
        setError(false);
      }
    }
  };
  const onBlur = (value: string) => {
    checkError(value);
    setTouched(true);
  };

  const optionValue = useMemo(
    () => row.placeholder || props.defaultTextTitle,
    [row.placeholder, props.defaultTextTitle],
  );
  // const [optionValue, setOptionValue] = useState(
  //   row.placeholder || props.defaultTextTitle,
  // );

  const handleChange = (value?: string) => {
    const val = props.type === "numberInput" && value != null ? +value : value;
    onChange({
      ...(typeof props.value === "object" &&
      !Array.isArray(props.value) &&
      props.value !== null
        ? props.value
        : {}),
      [row.uid]: !isNullish(value) ? { value: val } : null,
    });
    if (touched) checkError(value);
  };

  // useEffect(() => {
  //   props.updateText(optionValue, row.uid, "rows", undefined, "value");
  // }, [optionValue, row.uid]);

  const handlePlaceholderChange = (value?: string) => {
    props.updateItemText(value, row.uid, "rows", "placeholder");
  };

  const inputProps = useMemo<
    Omit<InputProps | NumberInputProps, "type" | "value"> & {
      inputType: "text" | "textarea" | "number";
    }
  >(() => {
    const baseProps = {
      ...props,
      onBlur: (e) => onBlur(e.target.value),
      inputType:
        (props.rows || 5) === 1 ? ("text" as const) : ("textarea" as const),
      error: !!error,
      note: error || undefined,
    };
    if (mode === "edit") {
      if (props.type === "numberInput") {
        return {
          ...baseProps,
          inputType: "text" as const,
        };
      }
      return {
        ...baseProps,
        inputType:
          (props.rows || 5) === 1 ? ("text" as const) : ("textarea" as const),
        rows: props.rows || 5,
        maxRows: props.maxRows || 10,
        className: "text-text-subdued",
        defaultValue: props.defaultTextTitle,
      };
    }
    if (props.type === "numberInput") {
      const { enabled, min, max } = props.settings?.limit || {};
      return {
        ...baseProps,
        min: enabled && min,
        max: enabled && max,
        inputType: "number" as const,
      };
    }
    const placeholder =
      (optionValue && translation?.[optionValue]) || optionValue;
    const { enabled, minChars, maxChars } =
      props.settings?.limitCharacters || {};
    return {
      ...baseProps,
      inputType:
        (props.rows || 5) === 1 ? ("text" as const) : ("textarea" as const),
      rows: props.rows || 5,
      maxRows: props.maxRows || 10,
      placeholder: `${placeholder}${props.settings?.required ? "" : ` (${t("global:optional").toLowerCase()})`}`,
      minLength: enabled ? minChars : undefined,
      maxLength: enabled ? maxChars : undefined,
    };
  }, [
    mode,
    error,
    optionValue,
    props.settings?.required,
    props.type,
    props.value?.[row.uid]?.value,
  ]);

  const onLabelChange = (e) =>
    props.updateItemText(e.target.value, row.uid, "rows", "label");

  return (
    <div key={row.uid} className="flex flex-col gap-0.5">
      {(props.options?.rows?.length || 0) > 1 ? (
        mode === "display" ? (
          <label htmlFor={row.uid} className="body-md-bold py-2">
            {row.label && (translation?.[row.label] || row.label)}
          </label>
        ) : (
          <EditableContent
            {...props}
            type="textarea"
            maxRows={1}
            className="body-md-bold"
            onChange={onLabelChange}
            value={row.label && (translation?.[row.label] || row.label)}
            placeholder={`${t("global:label")} (${t("global:optional").toLowerCase()})`}
            mode="edit"
          />
        )
      ) : null}

      <EditableContent
        key={row.uid}
        {...inputProps}
        type={inputProps.inputType}
        id={row.uid}
        value={mode === "edit" ? optionValue : props.value?.[row.uid]?.value}
        placeholder={
          mode === "edit" ? undefined : (optionValue ?? props.defaultTextTitle)
        }
        onChange={(event) =>
          mode === "edit"
            ? handlePlaceholderChange(event.target.value)
            : handleChange(event.target.value)
        }
        size={inputProps.inputType === "textarea" ? "md" : "xl"}
        silent={false}
        className={mode === "edit" ? "text-text-subdued" : undefined}
      />
    </div>
  );
};

const Text = ({
  className,
  mode,
  updateQuestion,
  onChange,
  ...props
}: TextProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      {props?.options?.rows?.map((row) => {
        return (
          <TextRow
            key={row.uid}
            {...props}
            row={row}
            mode={mode}
            onChange={onChange}
            formik={props.formikValues}
          />
        );
      })}
      {mode === "edit" && (
        <div className="mb-5 flex gap-2">
          <AddButton
            text={t("global:row")}
            onClick={() => props.addItem("rows")}
            show={mode === "edit"}
            id="pageColumns-addButton"
            disabled={!!maxItems(props, "rows")}
            disabledNotice={`Du kannst maximal ${maxItems(
              props,
              "rows",
            )} Optionen hinzufügen.`}
          />
          {(props.options?.rows?.length || 0) > 1 && (
            <AddButton
              text={t("global:row")}
              leadingIcon="minus-md"
              variant="outline"
              onClick={() =>
                props.options.rows &&
                props.removeItem(
                  props.options?.rows?.[props.options?.rows?.length - 1]?.uid,
                  "rows",
                )
              }
              show={mode === "edit"}
              id="pageColumns-addButton"
            />
          )}
        </div>
      )}
    </div>
  );
};
Text.displayName = "Text";
export default Text;
